import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Settings, X, Search, CheckSquare, Square, Sparkles, Layers, Package, Save, Library, Pencil, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";

// I 14 assi del configuratore (sincronizzati col backend LIBRARY_AXES_META)
const ALL_AXES = [
  { axis: "tessuti", label: "Tessuti", icona: "🧶" },
  { axis: "gambe", label: "Gambe", icona: "🦵" },
  { axis: "piedi", label: "Piedi (divani)", icona: "🦶" },
  { axis: "metalli", label: "Colori metallo", icona: "✨" },
  { axis: "piani", label: "Piani tavolo", icona: "🟫" },
  { axis: "basi", label: "Basi tavolo", icona: "🦴" },
  { axis: "finiture", label: "Finiture", icona: "🎨" },
  { axis: "maniglie", label: "Maniglie", icona: "🔘" },
  { axis: "dimensioni", label: "Dimensioni", icona: "📐" },
  { axis: "ante", label: "Ante", icona: "🚪" },
  { axis: "configurazioni", label: "Configurazioni divano", icona: "🛋️" },
  { axis: "giroletti", label: "Giroletti", icona: "🛏️" },
  { axis: "paralumi", label: "Paralumi", icona: "💡" },
  { axis: "cornici", label: "Cornici", icona: "🖼️" },
];

const fmtEuro = (n) => "€ " + (Number(n) || 0).toFixed(2).replace(".", ",");

export default function CasaMadreConfigEditor() {
  const [products, setProducts] = useState([]);
  const [axesStats, setAxesStats] = useState({});
  const [editing, setEditing] = useState(null); // product opened in editor
  const [pickerAxis, setPickerAxis] = useState(null); // axis currently in picker modal
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerSelected, setPickerSelected] = useState(new Set());
  const [pickerSearch, setPickerSearch] = useState("");
  const [selections, setSelections] = useState({}); // {axis: [codice, ...]}
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const loadAll = async () => {
    const [pRes, aRes] = await Promise.all([api.get("/products"), api.get("/libreria/axes")]);
    setProducts(pRes.data || []);
    const m = {};
    (aRes.data || []).forEach((a) => { m[a.axis] = a; });
    setAxesStats(m);
  };
  useEffect(() => { loadAll(); }, []);

  // Apre editor di un prodotto
  const openEditor = async (product) => {
    const r = await api.get(`/products/${product.id}/configurator-resolved`);
    setEditing(product);
    setSelections(r.data?.selections || {});
  };

  // Apre il picker (modale di selezione multipla) per un asse
  const openPicker = async (axis) => {
    const r = await api.get(`/libreria/${axis}`);
    setPickerAxis(axis);
    setPickerItems(r.data || []);
    setPickerSelected(new Set(selections[axis] || []));
    setPickerSearch("");
  };

  const confirmPicker = () => {
    setSelections((s) => ({ ...s, [pickerAxis]: Array.from(pickerSelected) }));
    setPickerAxis(null);
  };

  const removeChip = (axis, codice) => {
    setSelections((s) => ({ ...s, [axis]: (s[axis] || []).filter((c) => c !== codice) }));
  };

  // Salva selezioni del configuratore sul prodotto
  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/products/${editing.id}/configurator-selections`, { selections });
      toast.success("Configuratore salvato per " + editing.nome);
      setEditing(null);
      setSelections({});
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // Seed defaults per un asse (se libreria vuota)
  const seedDefaults = async (axis) => {
    if (!window.confirm(`Inserire le righe di default per "${axis}" nella tua libreria?`)) return;
    const r = await api.post(`/libreria/${axis}/seed-defaults`);
    toast.success(`${r.data.inserted} voci inserite in ${axis}`);
    if (pickerAxis === axis) openPicker(axis);
    loadAll();
  };

  // ─── Form "Aggiungi/Modifica voce alla libreria" (dentro il picker) ───
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null); // null = create; id = edit
  const EMPTY_ITEM = {
    codice: "", nome: "", livello: "", sovrapprezzo: 0,
    hex_color: "#cccccc", immagine_url: "",
    nome_colore: "", codice_colore: "",
    fabric_type: "fantasia", displacement_scale: 18,
  };
  const [newItem, setNewItem] = useState(EMPTY_ITEM);
  const resetNewItem = () => { setNewItem(EMPTY_ITEM); setEditingItemId(null); };

  const openEditForm = (it) => {
    setEditingItemId(it.id);
    setNewItem({
      codice: it.codice || "",
      nome: it.nome || "",
      livello: it.livello || "LVL1",
      sovrapprezzo: it.sovrapprezzo || 0,
      hex_color: it.hex_color || "#cccccc",
      immagine_url: it.immagine_url || "",
      nome_colore: it.nome_colore || "",
      codice_colore: it.codice_colore || "",
      fabric_type: it.fabric_type || "fantasia",
      displacement_scale: it.meta?.displacement_scale ?? 18,
    });
    setShowAddForm(true);
  };

  const uploadLibImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNewItem((n) => ({ ...n, immagine_url: r.data.url }));
      toast.success("Immagine caricata");
    } catch (e) {
      toast.error("Errore upload: " + (e.response?.data?.detail || e.message));
    }
  };

  const saveLibItem = async () => {
    if (!newItem.codice.trim()) { toast.error("Codice obbligatorio"); return; }
    if (pickerAxis === "tessuti" && newItem.fabric_type === "tinta_unita" && !newItem.hex_color) {
      toast.error("Per tinta unita serve l'HEX colore"); return;
    }
    try {
      const payload = {
        nome: newItem.nome || newItem.codice,
        livello: newItem.livello || "LVL1",
        sovrapprezzo: parseFloat(newItem.sovrapprezzo) || 0,
        hex_color: newItem.hex_color || "",
        immagine_url: newItem.immagine_url || "",
        nome_colore: newItem.nome_colore || "",
        codice_colore: newItem.codice_colore || "",
        meta: {},
      };
      if (pickerAxis === "tessuti") {
        payload.fabric_type = newItem.fabric_type;
        if (newItem.fabric_type === "materico") {
          payload.meta.displacement_scale = parseFloat(newItem.displacement_scale) || 18;
        }
      }
      let saved;
      if (editingItemId) {
        // EDIT: PUT (codice non modificabile dopo creazione per evitare conflitti)
        const r = await api.put(`/libreria/${pickerAxis}/${editingItemId}`, payload);
        saved = r.data;
        setPickerItems((p) => p.map((x) => x.id === saved.id ? saved : x));
        toast.success(`Voce "${saved.codice}" aggiornata`);
      } else {
        // CREATE
        payload.codice = newItem.codice.trim().toUpperCase();
        const r = await api.post(`/libreria/${pickerAxis}`, payload);
        saved = r.data;
        setPickerItems((p) => [...p, saved]);
        toast.success(`Voce "${saved.codice}" aggiunta`);
      }
      resetNewItem();
      setShowAddForm(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    }
  };

  const deleteLibItem = async (it) => {
    if (!window.confirm(`Eliminare la voce "${it.codice}" dalla libreria?`)) return;
    try {
      await api.delete(`/libreria/${pickerAxis}/${it.id}`);
      setPickerItems((p) => p.filter((x) => x.id !== it.id));
      setPickerSelected((s) => { const ns = new Set(s); ns.delete(it.codice); return ns; });
      toast.success(`Voce "${it.codice}" eliminata`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  // 🎨 Applica una grayscale (e fabric_type=tinta_unita) a tutti gli item filtrati
  // Tipico caso famiglia "Barrel" → 50 colori condividono la stessa grayscale macro
  const [showBulkApply, setShowBulkApply] = useState(false);
  const [bulkGrayscaleUrl, setBulkGrayscaleUrl] = useState("");
  const uploadBulkGrayscale = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkGrayscaleUrl(r.data.url);
      toast.success("Grayscale caricata — ora applicala");
    } catch (e) {
      toast.error("Errore upload: " + (e.response?.data?.detail || e.message));
    }
  };
  const applyBulkGrayscale = async (filteredItems) => {
    if (!bulkGrayscaleUrl) { toast.error("Carica prima una grayscale"); return; }
    if (!filteredItems.length) { toast.error("Nessun item da aggiornare"); return; }
    if (!window.confirm(`Impostare la grayscale e tipo "Tinta unita" su ${filteredItems.length} voci?`)) return;
    let done = 0;
    for (const it of filteredItems) {
      try {
        await api.put(`/libreria/${pickerAxis}/${it.id}`, {
          immagine_url: bulkGrayscaleUrl,
          fabric_type: "tinta_unita",
        });
        done++;
      } catch (e) { /* skip */ }
    }
    toast.success(`${done}/${filteredItems.length} voci aggiornate`);
    // Ricarica
    const r = await api.get(`/libreria/${pickerAxis}`);
    setPickerItems(r.data || []);
    setShowBulkApply(false);
    setBulkGrayscaleUrl("");
    loadAll();
  };

  const productsFiltered = products.filter((p) =>
    !q || p.nome.toLowerCase().includes(q.toLowerCase()) || p.sku_madre.toLowerCase().includes(q.toLowerCase())
  );

  const pickerItemsFiltered = useMemo(() => {
    if (!pickerSearch) return pickerItems;
    const qs = pickerSearch.toLowerCase();
    return pickerItems.filter((i) =>
      (i.codice || "").toLowerCase().includes(qs) ||
      (i.nome || "").toLowerCase().includes(qs) ||
      (i.nome_colore || "").toLowerCase().includes(qs) ||
      (i.livello || "").toLowerCase().includes(qs));
  }, [pickerItems, pickerSearch]);

  // Quanti item selezionati per asse — usato sui chip riassuntivi
  const countByAxis = (axis) => (selections[axis] || []).length;

  return (
    <AdminLayout title="Configuratore prodotti">
      <div className="max-w-[1500px] mx-auto p-6 lg:p-10">
        <div className="mb-8">
          <div className="overline text-brand mb-2">Casa Madre · Configuratore</div>
          <h1 className="font-heading font-bold text-4xl tracking-tight">Configura le opzioni dei tuoi prodotti</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-3xl">
            Apri un prodotto e scegli quali tessuti / gambe / metalli (o altri assi) sono disponibili.
            Il cliente vedrà SOLO le opzioni che hai abilitato e il prezzo si aggiornerà in tempo reale come <code className="text-xs px-1 bg-accent/40 rounded">prezzo_base + Σ sovrapprezzi</code>.
          </p>
        </div>

        {/* Librerie disponibili (riepilogo) */}
        <div className="bg-foreground text-white rounded-sm p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Library className="w-4 h-4" style={{ color: "#C6A664" }} />
            <div className="overline" style={{ color: "#C6A664" }}>Le tue librerie</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2" data-testid="libraries-summary">
            {ALL_AXES.map((a) => {
              const stat = axesStats[a.axis] || { total: 0, attivi: 0 };
              return (
                <div key={a.axis} className="text-center p-2 rounded-sm bg-black/30" data-testid={`libstat-${a.axis}`}>
                  <div className="text-xl mb-0.5">{a.icona}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/60">{a.label}</div>
                  <div className="font-heading font-bold text-lg mt-0.5">{stat.attivi}</div>
                  {stat.total === 0 && (
                    <button onClick={() => seedDefaults(a.axis)} data-testid={`seed-${a.axis}`}
                      className="text-[10px] underline text-white/60 hover:text-white mt-0.5">Popola</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Ricerca prodotti */}
        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="search-products" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca prodotto (nome o SKU)…" className="pl-9 h-11 rounded-sm" />
        </div>

        {/* Lista prodotti */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
          {productsFiltered.map((p) => {
            const sels = p.configurator_selections || {};
            const totalAxes = Object.keys(sels).filter((k) => (sels[k] || []).length > 0).length;
            return (
              <div key={p.id} className="bg-white border border-border rounded-sm p-5 hover:shadow-md hover:border-brand/40 transition-all" data-testid={`product-${p.sku_madre}`}>
                <div className="flex items-start gap-3 mb-3">
                  {p.immagini?.[0] ? (
                    <img src={p.immagini[0]} alt={p.nome} className="w-16 h-16 rounded-sm object-cover bg-accent/30" />
                  ) : (
                    <div className="w-16 h-16 rounded-sm bg-accent/30 flex items-center justify-center"><Package className="w-6 h-6 text-muted-foreground" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-lg leading-tight truncate">{p.nome}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{p.sku_madre}</div>
                    <div className="text-sm text-muted-foreground mt-1">{fmtEuro(p.prezzo_base || p.prezzo_consigliato_retail)} base</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mb-3 min-h-[20px]">
                  {totalAxes === 0 ? (
                    <span className="text-xs text-muted-foreground italic">Nessuna opzione configurata</span>
                  ) : (
                    Object.entries(sels).filter(([, v]) => v.length > 0).map(([axis, codes]) => {
                      const meta = ALL_AXES.find((a) => a.axis === axis);
                      return (
                        <span key={axis} className="px-2 py-0.5 bg-accent/40 rounded-full text-[11px]">
                          {meta?.icona} {meta?.label}: <strong>{codes.length}</strong>
                        </span>
                      );
                    })
                  )}
                </div>
                <button onClick={() => openEditor(p)} data-testid={`configure-${p.sku_madre}`}
                  className="w-full h-10 rounded-sm bg-foreground text-white text-sm font-medium hover:bg-brand transition-colors inline-flex items-center justify-center gap-2">
                  <Settings className="w-4 h-4" /> Configura opzioni
                </button>
              </div>
            );
          })}
        </div>

        {/* ─── Editor dialog (per prodotto) ─── */}
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="config-editor-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-brand" /> Configuratore — {editing?.nome}
              </DialogTitle>
              <DialogDescription>
                Aggiungi gli assi che vuoi rendere disponibili al cliente per questo prodotto. Per ogni asse, scegli quali opzioni della libreria includere.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {ALL_AXES.map((a) => {
                const selected = selections[a.axis] || [];
                const libCount = axesStats[a.axis]?.attivi || 0;
                if (selected.length === 0 && libCount === 0) return null;
                return (
                  <div key={a.axis} className="border border-border rounded-sm p-4" data-testid={`axis-block-${a.axis}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{a.icona}</span>
                        <div>
                          <div className="font-semibold text-sm">{a.label}</div>
                          <div className="text-[11px] text-muted-foreground">{selected.length} selezionati · {libCount} disponibili in libreria</div>
                        </div>
                      </div>
                      <button onClick={() => openPicker(a.axis)} data-testid={`open-picker-${a.axis}`}
                        disabled={libCount === 0}
                        className="h-8 px-3 rounded-sm border border-foreground text-foreground text-xs font-medium hover:bg-foreground hover:text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                        <Plus className="w-3.5 h-3.5" /> Aggiungi / Modifica
                      </button>
                    </div>
                    {selected.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {selected.map((codice) => (
                          <span key={codice} className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent/40 hover:bg-accent rounded-full text-xs" data-testid={`chip-${a.axis}-${codice}`}>
                            {codice}
                            <button onClick={() => removeChip(a.axis, codice)} className="hover:text-destructive" data-testid={`chip-remove-${a.axis}-${codice}`}>
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Bottoni per assi non ancora aggiunti */}
              <details className="border border-dashed border-border rounded-sm p-3">
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground hover:text-foreground" data-testid="more-axes-toggle">+ Aggiungi un altro tipo di opzione</summary>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                  {ALL_AXES.filter((a) => !(selections[a.axis] || []).length && (axesStats[a.axis]?.attivi || 0) > 0).map((a) => (
                    <button key={a.axis} onClick={() => openPicker(a.axis)} data-testid={`add-axis-${a.axis}`}
                      className="h-12 px-3 rounded-sm border border-border hover:border-brand hover:bg-accent/30 text-sm inline-flex items-center gap-2 justify-start">
                      <span className="text-lg">{a.icona}</span>
                      <span className="flex-1 text-left">{a.label}</span>
                      <span className="text-xs text-muted-foreground">{axesStats[a.axis]?.attivi}</span>
                    </button>
                  ))}
                  {ALL_AXES.filter((a) => !(axesStats[a.axis]?.total || 0)).map((a) => (
                    <button key={a.axis} onClick={() => seedDefaults(a.axis)} data-testid={`seed-axis-${a.axis}`}
                      className="h-12 px-3 rounded-sm border border-dashed border-border hover:border-brand text-sm inline-flex items-center gap-2 justify-start text-muted-foreground">
                      <span className="text-lg opacity-60">{a.icona}</span>
                      <span className="flex-1 text-left">Popola «{a.label}»</span>
                    </button>
                  ))}
                </div>
              </details>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-3 mt-2">
              <div className="text-xs text-muted-foreground">
                Prezzo base: <strong>{fmtEuro(editing?.prezzo_base || editing?.prezzo_consigliato_retail)}</strong> · in vetrina il cliente potrà combinare
                {" "}{Object.values(selections).reduce((acc, arr) => acc * Math.max(1, (arr || []).length), 1).toLocaleString("it-IT")} varianti
              </div>
              <button onClick={save} disabled={saving} data-testid="save-config"
                className="h-10 px-5 rounded-sm bg-foreground text-white text-sm font-medium hover:bg-brand disabled:opacity-50 inline-flex items-center gap-2">
                <Save className="w-4 h-4" /> {saving ? "Salvataggio…" : "Salva configuratore"}
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ─── Picker modal (per singolo asse) ─── */}
        <Dialog open={!!pickerAxis} onOpenChange={(o) => !o && setPickerAxis(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="picker-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="text-2xl">{ALL_AXES.find((a) => a.axis === pickerAxis)?.icona}</span>
                Scegli le opzioni: {ALL_AXES.find((a) => a.axis === pickerAxis)?.label}
              </DialogTitle>
              <DialogDescription>
                Clicca per selezionare / deselezionare. {pickerSelected.size} di {pickerItemsFiltered.length} selezionati.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)}
                    placeholder="Cerca per codice, nome, livello, colore…" className="pl-9 h-10 rounded-sm" data-testid="picker-search" />
                </div>
                <button onClick={() => {
                  const all = pickerItemsFiltered.filter((i) => i.attivo).map((i) => i.codice);
                  const allSel = all.every((c) => pickerSelected.has(c));
                  setPickerSelected(allSel ? new Set([...pickerSelected].filter((c) => !all.includes(c))) : new Set([...pickerSelected, ...all]));
                }} data-testid="picker-toggle-all"
                  className="h-10 px-3 rounded-sm border border-border text-sm hover:bg-accent">
                  Seleziona tutti i filtrati
                </button>
                <button onClick={() => { setShowAddForm((v) => !v); if (showAddForm) resetNewItem(); }} data-testid="picker-toggle-add"
                  className="h-10 px-3 rounded-sm border border-brand bg-brand/10 text-sm hover:bg-brand/20 text-brand font-medium inline-flex items-center gap-1">
                  <Plus className="w-4 h-4" /> {showAddForm ? "Chiudi" : "Aggiungi voce"}
                </button>
                {pickerAxis === "tessuti" && (
                  <button onClick={() => setShowBulkApply((v) => !v)} data-testid="picker-toggle-bulk"
                    className="h-10 px-3 rounded-sm border border-amber-500 bg-amber-50 text-sm hover:bg-amber-100 text-amber-800 font-medium inline-flex items-center gap-1" title="Applica una grayscale a tutta una famiglia di tessuti (es. Barrel)">
                    <Wand2 className="w-4 h-4" /> Bulk grayscale
                  </button>
                )}
              </div>

              {/* Form Bulk: applica grayscale a tutti gli item filtrati */}
              {showBulkApply && pickerAxis === "tessuti" && (
                <div className="border border-amber-300 bg-amber-50 rounded-sm p-4 space-y-3" data-testid="picker-bulk-form">
                  <div className="text-xs font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1">
                    <Wand2 className="w-3.5 h-3.5" /> Applica grayscale a famiglia
                  </div>
                  <p className="text-xs text-amber-900 leading-relaxed">
                    Carica la texture <strong>grayscale</strong> (es. trama macro di &quot;BARREL&quot;) e cliccando &quot;Applica&quot; verrà
                    impostata su <strong>tutte le voci filtrate</strong> in basso, marcandole come <em>Tinta unita</em>.
                    Suggerimento: prima usa la ricerca per filtrare solo la famiglia (es. cerca &quot;BARREL&quot;).
                  </p>
                  <div className="flex gap-3 items-center">
                    <input type="file" accept="image/*" onChange={(e) => uploadBulkGrayscale(e.target.files?.[0])}
                      className="text-xs flex-1" data-testid="bulk-grayscale-file" />
                    {bulkGrayscaleUrl && (
                      <img src={bulkGrayscaleUrl} alt="grayscale" className="w-12 h-12 rounded-sm object-cover border border-amber-300" />
                    )}
                    <button onClick={() => applyBulkGrayscale(pickerItemsFiltered)} disabled={!bulkGrayscaleUrl}
                      data-testid="bulk-apply-btn"
                      className="h-9 px-4 rounded-sm bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1">
                      <Wand2 className="w-3.5 h-3.5" /> Applica a {pickerItemsFiltered.length} voci
                    </button>
                  </div>
                </div>
              )}

              {/* Form inline per aggiungere/modificare una voce */}
              {showAddForm && (
                <div className="border border-brand/30 bg-brand/5 rounded-sm p-4 space-y-3" data-testid="picker-add-form">
                  <div className="text-xs font-bold uppercase tracking-wider text-brand flex items-center gap-1">
                    {editingItemId ? <><Pencil className="w-3.5 h-3.5" /> Modifica voce: {newItem.codice}</> : <><Plus className="w-3.5 h-3.5" /> Nuova voce libreria</>}
                  </div>
                  {pickerAxis === "tessuti" && (
                    <div>
                      <label className="block text-[11px] font-semibold mb-1">Tipo tessuto</label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { v: "tinta_unita", t: "Tinta unita", d: "Texture B/N + colore HEX" },
                          { v: "fantasia", t: "Fantasia", d: "Texture stampata a colori" },
                          { v: "materico", t: "Materico", d: "Effetto rilievo (displacement)" },
                        ].map((opt) => (
                          <button key={opt.v} type="button" onClick={() => setNewItem((n) => ({ ...n, fabric_type: opt.v }))}
                            data-testid={`fabric-type-${opt.v}`}
                            className={`text-left p-2 rounded-sm border text-xs transition-all ${newItem.fabric_type === opt.v ? "border-brand bg-brand/10 ring-1 ring-brand/30" : "border-border hover:border-foreground bg-white"}`}>
                            <div className="font-bold">{opt.t}</div>
                            <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">{opt.d}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold mb-1">Codice <span className="text-red-600">*</span></label>
                      <Input value={newItem.codice} onChange={(e) => setNewItem((n) => ({ ...n, codice: e.target.value }))}
                        placeholder="es. BARREL-19" className="h-9 rounded-sm" disabled={!!editingItemId} data-testid="newitem-codice" />
                      {editingItemId && <div className="text-[10px] text-muted-foreground mt-0.5">Il codice non è modificabile dopo la creazione</div>}
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold mb-1">Nome / Etichetta</label>
                      <Input value={newItem.nome} onChange={(e) => setNewItem((n) => ({ ...n, nome: e.target.value }))}
                        placeholder="es. Barrel Beige" className="h-9 rounded-sm" data-testid="newitem-nome" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold mb-1">Livello prezzo</label>
                      <select value={newItem.livello} onChange={(e) => setNewItem((n) => ({ ...n, livello: e.target.value }))}
                        className="w-full h-9 rounded-sm border border-input bg-white px-2 text-sm" data-testid="newitem-livello">
                        {["LVL1","LVL2","LVL3","LVL4","LVL5"].map((l) => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold mb-1">Sovrapprezzo €</label>
                      <Input type="number" step="0.10" value={newItem.sovrapprezzo}
                        onChange={(e) => setNewItem((n) => ({ ...n, sovrapprezzo: e.target.value }))}
                        className="h-9 rounded-sm" data-testid="newitem-sovrapprezzo" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold mb-1">
                        Colore HEX {pickerAxis === "tessuti" && newItem.fabric_type === "tinta_unita" && <span className="text-red-600">*</span>}
                      </label>
                      <div className="flex gap-1">
                        <input type="color" value={newItem.hex_color} onChange={(e) => setNewItem((n) => ({ ...n, hex_color: e.target.value }))}
                          className="h-9 w-12 rounded-sm border border-input cursor-pointer" data-testid="newitem-hexpicker" />
                        <Input value={newItem.hex_color} onChange={(e) => setNewItem((n) => ({ ...n, hex_color: e.target.value }))}
                          className="h-9 rounded-sm font-mono text-xs" data-testid="newitem-hextext" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold mb-1">
                      Immagine texture {pickerAxis === "tessuti" && newItem.fabric_type === "tinta_unita" && <span className="text-muted-foreground">(grayscale, opzionale ma consigliata)</span>}
                    </label>
                    <div className="flex gap-2 items-start">
                      <input type="file" accept="image/*" onChange={(e) => uploadLibImage(e.target.files?.[0])}
                        className="text-xs flex-1" data-testid="newitem-imgfile" />
                      {newItem.immagine_url && (
                        <div className="relative">
                          {newItem.fabric_type === "tinta_unita" ? (
                            <svg width="48" height="48" className="rounded-sm border border-border">
                              <defs>
                                <filter id="prevTint">
                                  <feColorMatrix type="matrix" values={`${parseInt(newItem.hex_color.slice(1,3),16)/255} 0 0 0 0  ${parseInt(newItem.hex_color.slice(3,5),16)/255} 0 0 0 0  ${parseInt(newItem.hex_color.slice(5,7),16)/255} 0 0 0 0  0 0 0 1 0`} />
                                </filter>
                              </defs>
                              <image href={newItem.immagine_url} width="48" height="48" preserveAspectRatio="xMidYMid slice" filter="url(#prevTint)" />
                            </svg>
                          ) : (
                            <img src={newItem.immagine_url} alt="preview" className="w-12 h-12 rounded-sm object-cover border border-border" />
                          )}
                          <button onClick={() => setNewItem((n) => ({ ...n, immagine_url: "" }))} className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white rounded-full text-[9px] flex items-center justify-center" type="button">×</button>
                        </div>
                      )}
                    </div>
                  </div>
                  {pickerAxis === "tessuti" && newItem.fabric_type === "materico" && (
                    <div>
                      <label className="block text-[11px] font-semibold mb-1">Intensità rilievo (0-40)</label>
                      <input type="range" min="0" max="40" step="1" value={newItem.displacement_scale}
                        onChange={(e) => setNewItem((n) => ({ ...n, displacement_scale: e.target.value }))}
                        className="w-full" data-testid="newitem-displacement" />
                      <div className="text-[10px] text-muted-foreground text-right">{newItem.displacement_scale}</div>
                    </div>
                  )}
                  <div className="flex justify-end gap-2 pt-2 border-t border-brand/20">
                    <button onClick={() => { resetNewItem(); setShowAddForm(false); }} type="button"
                      className="h-9 px-3 rounded-sm border border-border text-xs hover:bg-accent">Annulla</button>
                    <button onClick={saveLibItem} type="button" data-testid="newitem-save"
                      className="h-9 px-4 rounded-sm bg-brand text-white text-xs font-medium hover:opacity-90 inline-flex items-center gap-1">
                      {editingItemId ? <><Save className="w-3.5 h-3.5" /> Salva modifiche</> : <><Plus className="w-3.5 h-3.5" /> Salva voce</>}
                    </button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-[55vh] overflow-y-auto pr-2">
                {pickerItemsFiltered.length === 0 ? (
                  <div className="col-span-full text-center text-muted-foreground py-12">
                    Nessuna voce trovata. {pickerItems.length === 0 && <button onClick={() => seedDefaults(pickerAxis)} className="text-brand underline ml-1">Popola libreria</button>}
                  </div>
                ) : pickerItemsFiltered.map((it) => {
                  const checked = pickerSelected.has(it.codice);
                  const isTinta = pickerAxis === "tessuti" && it.fabric_type === "tinta_unita";
                  const toggleSelect = (e) => {
                    e.stopPropagation();
                    const ns = new Set(pickerSelected);
                    if (ns.has(it.codice)) ns.delete(it.codice); else ns.add(it.codice);
                    setPickerSelected(ns);
                  };
                  return (
                    <div key={it.id} onClick={toggleSelect} data-testid={`picker-item-${it.codice}`}
                      className={`flex items-center gap-3 p-2 rounded-sm border text-left transition-all cursor-pointer ${checked ? "border-brand bg-brand/5 ring-1 ring-brand/30" : "border-border hover:border-foreground"} ${!it.attivo ? "opacity-50" : ""}`}>
                      {checked ? <CheckSquare className="w-5 h-5 text-brand flex-shrink-0" /> : <Square className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                      {/* Mini swatch: per tinta unita mostra il colore HEX (la grayscale non aiuta) */}
                      {isTinta && it.hex_color ? (
                        <div className="w-10 h-10 rounded-sm flex-shrink-0 border border-border" style={{ background: it.hex_color }} />
                      ) : it.immagine_url ? (
                        <img src={it.immagine_url} alt={it.codice} className="w-10 h-10 rounded-sm object-cover bg-accent/30 flex-shrink-0" />
                      ) : it.hex_color ? (
                        <div className="w-10 h-10 rounded-sm flex-shrink-0 border border-border" style={{ background: it.hex_color }} />
                      ) : (
                        <div className="w-10 h-10 rounded-sm bg-accent/40 flex-shrink-0 flex items-center justify-center text-xs">{it.codice.slice(0, 2)}</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{it.nome || it.codice}</div>
                        <div className="text-[10px] text-muted-foreground truncate flex items-center gap-1">
                          {it.livello && <span className="font-semibold">{it.livello}</span>}
                          {pickerAxis === "tessuti" && it.fabric_type && it.fabric_type !== "fantasia" && (
                            <span className={`px-1 rounded text-[9px] font-bold uppercase ${it.fabric_type === "tinta_unita" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                              {it.fabric_type === "tinta_unita" ? "Tinta" : "Materico"}
                            </span>
                          )}
                          <span className="truncate">{it.nome_colore || it.codice_colore || it.codice}</span>
                        </div>
                      </div>
                      <div className="text-[10px] font-semibold text-right flex-shrink-0">{it.sovrapprezzo > 0 ? "+ " + fmtEuro(it.sovrapprezzo) : "—"}</div>
                      {/* Edit / Delete icons */}
                      <div className="flex flex-col gap-0.5 flex-shrink-0">
                        <button onClick={(e) => { e.stopPropagation(); openEditForm(it); }} data-testid={`picker-edit-${it.codice}`}
                          title="Modifica voce"
                          className="w-6 h-6 rounded-sm hover:bg-blue-100 text-blue-700 inline-flex items-center justify-center">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); deleteLibItem(it); }} data-testid={`picker-delete-${it.codice}`}
                          title="Elimina voce"
                          className="w-6 h-6 rounded-sm hover:bg-red-100 text-red-600 inline-flex items-center justify-center">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-3">
              <div className="text-xs text-muted-foreground"><strong>{pickerSelected.size}</strong> opzioni scelte per questo prodotto</div>
              <div className="flex gap-2">
                <button onClick={() => setPickerAxis(null)} data-testid="picker-cancel"
                  className="h-10 px-4 rounded-sm border border-border text-sm hover:bg-accent">Annulla</button>
                <button onClick={confirmPicker} data-testid="picker-confirm"
                  className="h-10 px-5 rounded-sm bg-foreground text-white text-sm font-medium hover:bg-brand">Conferma selezione</button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
